import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState, useEffect } from "react";
import { useHeatmapEvents, useFeatureFlag, type HeatPoint } from "@/lib/analytics";
import { HeatmapLayer } from "@/components/HeatmapLayer";
import { Locked } from "./analytics.index";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Flame, Layers, Play, Pause } from "lucide-react";

export const Route = createFileRoute("/_authenticated/analytics/heatmaps")({
  component: HeatmapsPage,
});

const SOURCES = [
  { key: "gps", label: "GPS", color: "bg-success/20 text-success" },
  { key: "scan", label: "Scans", color: "bg-primary/20 text-primary" },
  { key: "incident", label: "Incidents", color: "bg-destructive/20 text-destructive" },
  { key: "geofence", label: "Geofence", color: "bg-warning/20 text-warning" },
  { key: "dispatch", label: "Dispatch", color: "bg-accent/20 text-accent-foreground" },
] as const;

const RANGES = [
  { key: "24h", label: "24h", hours: 24 },
  { key: "7d", label: "7d", hours: 24 * 7 },
  { key: "30d", label: "30d", hours: 24 * 30 },
] as const;

function HeatmapsPage() {
  const enabled = useFeatureFlag("advanced_analytics");
  const { currentOrg } = useAuth();
  const [range, setRange] = useState<(typeof RANGES)[number]["key"]>("7d");
  const [active, setActive] = useState<string[]>(["gps", "incident", "scan", "geofence"]);
  const [siteId, setSiteId] = useState<string>("");
  const [intensity, setIntensity] = useState(1);
  const [radius, setRadius] = useState(28);
  const [showHeat, setShowHeat] = useState(true);
  const [showMarkers, setShowMarkers] = useState(false);
  const [showFences, setShowFences] = useState(true);

  // Playback
  const [playPos, setPlayPos] = useState(100);
  const [playing, setPlaying] = useState(false);

  const fromDate = useMemo(() => {
    const r = RANGES.find((x) => x.key === range)!;
    return new Date(Date.now() - r.hours * 3600 * 1000).toISOString();
  }, [range]);

  const { data: events = [], isLoading } = useHeatmapEvents({
    sources: active,
    siteId: siteId || null,
    fromDate,
    limit: 8000,
  });

  const [sites, setSites] = useState<Array<{ id: string; name: string }>>([]);
  const [geofences, setGeofences] = useState<any[]>([]);
  useEffect(() => {
    if (!currentOrg) return;
    supabase.from("sites").select("id,name").eq("organization_id", currentOrg.id).eq("active", true)
      .then(({ data }) => setSites(data ?? []));
    supabase.from("geofences").select("*").eq("organization_id", currentOrg.id).eq("active", true)
      .then(({ data }) => setGeofences(data ?? []));
  }, [currentOrg?.id]);

  // Playback animation
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => {
      setPlayPos((p) => (p >= 100 ? 5 : p + 2));
    }, 120);
    return () => clearInterval(id);
  }, [playing]);

  const points: HeatPoint[] = useMemo(() => {
    if (events.length === 0) return [];
    const sorted = [...events].sort((a, b) => +new Date(a.occurred_at) - +new Date(b.occurred_at));
    const cutoffIdx = Math.max(1, Math.floor((sorted.length * playPos) / 100));
    return sorted.slice(0, cutoffIdx).map((e) => ({
      lat: Number(e.latitude),
      lng: Number(e.longitude),
      weight: Number(e.weight) || 1,
    }));
  }, [events, playPos]);

  if (!enabled.isLoading && !enabled.data) return <Locked />;

  const toggleSource = (k: string) =>
    setActive((a) => (a.includes(k) ? a.filter((x) => x !== k) : [...a, k]));

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-4">
        <div className="flex gap-1 rounded-md border border-border p-1">
          {RANGES.map((r) => (
            <button key={r.key} onClick={() => setRange(r.key)}
              className={`rounded px-3 py-1 text-xs ${range === r.key ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              {r.label}
            </button>
          ))}
        </div>

        <select value={siteId} onChange={(e) => setSiteId(e.target.value)}
          className="rounded-md border border-border bg-background px-3 py-1.5 text-sm">
          <option value="">All sites</option>
          {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        <div className="flex flex-wrap items-center gap-1.5">
          {SOURCES.map((s) => {
            const on = active.includes(s.key);
            return (
              <button key={s.key} onClick={() => toggleSource(s.key)}
                className={`rounded-full px-2.5 py-1 text-xs transition-opacity ${s.color} ${on ? "" : "opacity-30"}`}>
                {s.label}
              </button>
            );
          })}
        </div>

        <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={showHeat} onChange={(e) => setShowHeat(e.target.checked)} /> Heat
          </label>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={showMarkers} onChange={(e) => setShowMarkers(e.target.checked)} /> Points
          </label>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={showFences} onChange={(e) => setShowFences(e.target.checked)} /> Geofences
          </label>
        </div>
      </div>

      {/* Map */}
      <HeatmapLayer
        points={points}
        geofences={showFences ? geofences : []}
        intensity={intensity}
        radius={radius}
        showHeat={showHeat}
        showMarkers={showMarkers}
        showGeofences={showFences}
        height={520}
      />

      {/* Sliders */}
      <div className="grid gap-4 rounded-xl border border-border bg-card p-4 sm:grid-cols-3">
        <SliderRow label="Intensity" value={intensity} min={0.2} max={2} step={0.1} onChange={setIntensity} />
        <SliderRow label="Radius" value={radius} min={10} max={60} step={2} onChange={setRadius} />
        <div>
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Playback ({playPos}%)</span>
            <button onClick={() => setPlaying((p) => !p)} className="inline-flex items-center gap-1 rounded border border-border px-2 py-0.5 text-xs">
              {playing ? <><Pause className="h-3 w-3" /> Pause</> : <><Play className="h-3 w-3" /> Play</>}
            </button>
          </div>
          <input type="range" min={5} max={100} step={1} value={playPos}
            onChange={(e) => { setPlaying(false); setPlayPos(Number(e.target.value)); }} className="w-full" />
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        <Flame className="mr-1 inline h-3 w-3" />
        {isLoading ? "Loading…" : `${points.length.toLocaleString()} of ${events.length.toLocaleString()} events plotted`} · realtime
      </p>
    </div>
  );
}

function SliderRow({ label, value, min, max, step, onChange }: {
  label: string; value: number; min: number; max: number; step: number; onChange: (v: number) => void;
}) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
        <span><Layers className="mr-1 inline h-3 w-3" /> {label}</span>
        <span>{value}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))} className="w-full" />
    </div>
  );
}
